import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { GameProfileAdminClient } from 'skychain-game-profile-js/lib/admin/game-profile-admin-client';
import { PageOptions } from 'skychain-game-profile-js/lib/dto/base.query';
import {
	IRefAuditQuery,
	REF_TRANSACTION_TYPES,
	REF_TRANSACTION_STATUS,
} from 'skychain-game-profile-js/lib/dto/ref.query';
import styled from 'styled-components';
import { date_format } from '../../../config';
import { setIsLoading } from '../../../redux/reducers/system.slice';
import { RootState, useAppDispatch } from '../../../redux/store';
import { formatCurrency } from '../../../utils';
import Copy from '../../UI/Copy';
import CustomTable from '../../UI/CustomTable';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../routes/config';

const CopyWrapper = styled('div')`
	display: flex;
	align-items: center;
	gap: 5px;
	p {
		color: #1c84e4;
		font-size: 14px;
		line-height: 16.1px;
	}
`;

interface DataType {
	userEmail: string;
	id: string;
	userId: string;
	date: string;
	amount: string;
	status: string;
	type: string;
}

interface AuditCommissionTableProps {
	query: IRefAuditQuery;
	params: PageOptions;
	setParams: (params: PageOptions) => void;
}

const TypeText = styled('p')<any>`
	background: ${(props) =>
		props.type === REF_TRANSACTION_TYPES.CASHBACK
			? 'linear-gradient(138.96deg, #f7931e 1.86%, #fbb03b 65.6%)'
			: props.type === REF_TRANSACTION_TYPES.SAMELEVEL
			? 'linear-gradient(270deg, #8B6BE6 -30.6%, #E2589A 134.33%)'
			: 'linear-gradient(270deg, #78BD44 -34.33%, #29A961 135.68%)'};
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
	text-fill-color: transparent;
	font-size: 14px;
	line-height: 16.1px;
	text-transform: capitalize;
`;

const StatusText = styled('p')<any>`
	background: ${(props) =>
		props.status === REF_TRANSACTION_STATUS.PENDING
			? 'linear-gradient(138.96deg, #f7931e 1.86%, #fbb03b 65.6%)'
			: props.status === REF_TRANSACTION_STATUS.FAILED
			? 'linear-gradient(270deg, #FF5151 -31.34%, #FE474E 136.57%);'
			: props.status === REF_TRANSACTION_STATUS.WAITING
			? 'linear-gradient(270deg, #78BD44 -34.33%, #29A961 135.68%)'
			: 'linear-gradient(270deg, #37CBFF -30.6%, #0094DB 133.58%);'};
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
	text-fill-color: transparent;
	font-size: 14px;
	line-height: 16.1px;
	text-transform: capitalize;
`;

const columns: ColumnsType<DataType> = [
	{
		title: 'User',
		dataIndex: 'userEmail',
		key: 'userEmail',
		render: (value) => {
			return (
				<CopyWrapper>
					<p>{value}</p>
					{value && <Copy text={value} />}
				</CopyWrapper>
			);
		},
	},
	{
		title: 'Amount',
		dataIndex: 'amount',
		key: 'amount',
	},
	{
		title: 'Date',
		dataIndex: 'date',
		key: 'date',
	},

	{
		title: 'Type',
		dataIndex: 'type',
		key: 'type',
		render: (value: string) => {
			return (
				<TypeText type={value}>
					{value.replaceAll('_', ' ').toLowerCase()}
				</TypeText>
			);
		},
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		render: (value: string) => {
			return <StatusText status={value}>{value.toLowerCase()}</StatusText>;
		},
	},
	{
		title: 'Action',
		render: (value, record) => {
			return (
				<Link
					style={{
						color: '#737283',
					}}
					to={ROUTES.FRANCHISE_AUDIT_COMMISION_DETAIL.replace(':id', record.id)}
				>
					Detail
				</Link>
			);
		},
	},
];
const AuditCommissionTable: React.FC<AuditCommissionTableProps> = ({
	query,
	params,
	setParams,
}: AuditCommissionTableProps) => {
	const gameProfile = useSelector(
		(state: RootState) => state.system.gameProfile
	);
	const dispatch = useAppDispatch();
	const [commissions, setCommissions] = React.useState<DataType[]>([]);

	const [total, setTotal] = useState(0);
	useEffect(() => {
		if (gameProfile) {
			getCommissionHistory(gameProfile, query, params);
		}
	}, [gameProfile, params, query]);

	const getCommissionHistory = async (
		gameProfile: GameProfileAdminClient,
		query: IRefAuditQuery,
		params?: PageOptions
	) => {
		dispatch(setIsLoading(true));
		try {
			const res = await gameProfile.getRefPaymentList(query, params);
			if (res.data) {
				const data: DataType[] = res.data.map((item) => {
					return {
						id: item.id,
						userId: item.userId,
						userEmail: item.email || '',
						date: format(Number(item.createdAt) * 1000, date_format),
						amount: `${formatCurrency(item.amount, 3)}`,
						status: item.status || '',
						type: item.type || '',
					};
				});
				setCommissions(data);
			}

			setTotal(res.meta.totalRecord);
			dispatch(setIsLoading(false));
		} catch (error) {
			dispatch(setIsLoading(false));
			console.log('error', error);
		}
	};

	const onChangeTable = (pagination: TablePaginationConfig) => {
		setParams({
			page: pagination.current || 1,
			pageSize: pagination.pageSize || 10,
		});
	};
	return (
		<CustomTable
			columns={columns}
			dataSource={commissions}
			onChange={onChangeTable}
			pageSize={params.pageSize}
			total={total}
			current={params.page}
		/>
	);
};

export default AuditCommissionTable;
